import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowRight, FiChevronDown } from "react-icons/fi";
import { BiBook, BiGroup, BiBuilding, BiTime } from "react-icons/bi";
import { useSiteSettings } from "../../context/SiteSettingsContext";

const stats = [
  { icon: BiGroup,    value: "1500+", label: "Students" },
  { icon: BiBook,     value: "80+",   label: "Teachers" },
  { icon: BiBuilding, value: "30+",   label: "Classrooms" },
  { icon: BiTime,     value: "25+",   label: "Years" },
];

const HeroSection = () => {
  const { settings } = useSiteSettings();
  const heroMedia =
    settings?.heroMedia?.mediaType &&
    settings?.heroMedia?.mediaType !== "none" &&
    settings?.heroMedia?.url
      ? settings.heroMedia
      : null;

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#1e3a8a]">
      {/* Background media */}
      {heroMedia?.mediaType === "video" && (
        <video autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-80"
          style={{ zIndex: 1 }} src={heroMedia.url} />
      )}
      {heroMedia?.mediaType === "image" && (
        <img src={heroMedia.url} alt="School"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
          style={{ zIndex: 1 }} />
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-[#1e3a8a]/35" style={{ zIndex: 2 }} />

      {/* Top white accent line */}
      <div className="absolute left-0 top-0 right-0 h-1 bg-white/30" style={{ zIndex: 4 }} />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 pt-28 sm:pt-32 pb-24 sm:pb-32 flex flex-col items-center text-center w-full" style={{ zIndex: 5 }}>

        {/* Badge */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-5 py-2 bg-white/15 border border-white/30 text-white text-xs font-semibold uppercase tracking-[0.18em] mb-8 rounded-full backdrop-blur-sm">
          <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse shrink-0" />
          Admissions Open 2025-2026
        </motion.div>

        {/* School Name */}
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-wide mb-5">
          {settings?.schoolName ? settings.schoolName.split(" ")[0] : "Early"}{" "}
          <span className="text-sky-300">
            {settings?.schoolName
              ? settings.schoolName.substring(settings.schoolName.indexOf(" ") + 1)
              : "Boarding School"}
          </span>
        </motion.h1>

        {/* Tagline */}
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
          className="text-base sm:text-lg lg:text-xl text-blue-100/80 font-medium mb-10 max-w-xl leading-relaxed">
          {settings?.schoolTagline || "Inspiring Excellence in Education"}
        </motion.p>

        {/* Buttons */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 mb-16 sm:mb-20 justify-center">
          <Link to="/admissions/apply"
            className="flex items-center justify-center gap-3 px-8 py-4 bg-white text-[#1D4ED8] font-bold uppercase tracking-wide text-sm hover:bg-blue-50 transition-all duration-200 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5">
            Apply Now <FiArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/academics"
            className="flex items-center justify-center gap-3 px-8 py-4 bg-white/10 text-white font-bold uppercase tracking-wide text-sm hover:bg-white/20 transition-all duration-200 border border-white/40 rounded-xl hover:-translate-y-0.5">
            Explore Programs
          </Link>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full max-w-3xl">
          {stats.map((stat, idx) => (
            <div key={idx}
              className="bg-white/10 border border-white/20 backdrop-blur-sm rounded-2xl p-5 sm:p-6 flex flex-col items-center text-center hover:bg-white/18 hover:border-sky-300/50 transition-all duration-300 group">
              <stat.icon className="w-6 h-6 sm:w-7 sm:h-7 text-blue-200/60 group-hover:text-sky-300 transition-colors mb-3" />
              <div className="text-3xl sm:text-4xl font-black text-white font-mono mb-1">{stat.value}</div>
              <div className="text-blue-200/60 text-xs font-semibold uppercase tracking-widest">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
        className="absolute bottom-8 right-8 text-white/50 cursor-pointer" style={{ zIndex: 5 }}>
        <FiChevronDown className="w-8 h-8" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
